import {
  Avatar,
  AvatarGroup,
  Collection,
  CollectionItem,
  Directory,
  DirectoryRow,
  Pushbutton,
  Pushtext
} from '@stratumn/atomic';

import graphql from 'babel-plugin-relay/macro';
import { ROUTE_EDIT_BOT_PROFILE, ROUTE_TEAM } from 'constant/routes';
import compose from 'lodash.flowright';
import PropType from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import injectSheet from 'react-jss';
import { createFragmentContainer } from 'react-relay';
import { generatePath, Link, withRouter } from 'react-router-dom';
import nameToInitials from 'utils/nameToInitials';
import { withGqlClient } from 'wrappers';

import AddBotKeyModal from './addBotKeyModal';
import styles from './bot.style';
import ExpireKeyModal from './expireKeyModal';
import Keys from './keys';
import RemoveOrgBotModal from './removeOrgBotModal';
import RemoveTeamBotModal from './removeTeamBotModal';

export const Bot = ({ classes, bot }) => {
  const [showModalOrg, setShowModalOrg] = useState(false);
  const [showModalTeam, setShowModalTeam] = useState(false);
  const [showModalAddKey, setShowModalAddKey] = useState(false);
  const [showModalExpireKey, setShowModalExpireKey] = useState(false);
  const [team, setTeam] = useState(null);
  const [expiredKey, setExpiredKey] = useState(null);

  const pkTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (pkTimeoutRef.current) {
        clearTimeout(pkTimeoutRef.current);
      }
    },
    []
  );

  const toggleModalOrg = () => setShowModalOrg(prev => !prev);

  const toggleModalTeam = teamBot => {
    setTeam(teamBot);
    setShowModalTeam(prev => !prev);
  };

  const toggleModalAddKey = () => setShowModalAddKey(prev => !prev);

  const toggleModalExpireKey = expiredKeyBot => {
    setExpiredKey(expiredKeyBot);
    setShowModalExpireKey(prev => !prev);
  };

  const renderTeamRow = (node, index) => {
    const { id, rowId, name, avatar, canUpdate } = node;
    const {
      teams: { totalCount },
      organization: { rowId: orgRowId }
    } = bot;

    return (
      <DirectoryRow
        cell1={
          canUpdate && (
            <Pushtext
              data-testid="remove-from-team"
              onClick={() => toggleModalTeam(node)}
            >
              Remove
            </Pushtext>
          )
        }
        isLast={index === totalCount - 1}
        key={id}
        main={
          <Link
            component={({ navigate }) => (
              <Pushtext onClick={() => navigate()}>
                <div className={classes.link}>
                  <AvatarGroup
                    initials={nameToInitials(name)}
                    nth={Number(rowId)}
                    size={37}
                    src={avatar}
                  />
                  <span className={classes.teamName}>{name}</span>
                </div>
              </Pushtext>
            )}
            to={generatePath(ROUTE_TEAM, { id: rowId, orgId: orgRowId })}
          />
        }
      />
    );
  };

  return (
    <>
      <div className={classes.userHeaderWrapper}>
        <div>
          <Avatar size={56} src={bot.avatar} />
        </div>
        {bot.organization.canUpdate && (
          <div className={classes.updateActions}>
            <div className={classes.removeAction}>
              <Pushtext
                data-testid="remove-from-org"
                mute
                onClick={toggleModalOrg}
                warning
              >
                Remove from organization...
              </Pushtext>
            </div>
            <Link
              component={({ navigate }) => (
                <Pushbutton
                  data-testid="edit-bot"
                  onClick={() => navigate()}
                  secondary
                >
                  Edit Bot
                </Pushbutton>
              )}
              to={generatePath(ROUTE_EDIT_BOT_PROFILE, { id: bot.rowId })}
            />
          </div>
        )}
      </div>
      {showModalTeam && (
        <RemoveTeamBotModal
          bot={bot}
          team={team}
          toggleModal={toggleModalTeam}
        />
      )}
      {showModalOrg && (
        <RemoveOrgBotModal bot={bot} toggleModal={toggleModalOrg} />
      )}
      {showModalAddKey && (
        <AddBotKeyModal bot={bot} toggleModal={toggleModalAddKey} />
      )}
      {showModalExpireKey && (
        <ExpireKeyModal
          keyName={expiredKey?.name}
          keyObj={expiredKey?.key}
          toggleModal={toggleModalExpireKey}
        />
      )}
      <Collection>
        <div className={classes.collectionItemFirstChild}>
          <CollectionItem idle={!bot.name} label="bot name" value={bot.name} />
        </div>
        {bot.description && (
          <CollectionItem label="description" value={bot.description} />
        )}
        <div className={classes.teamsList}>
          <Directory columnTitle="teams" showColumnTitles>
            {bot.teams.nodes.map((node, index) => renderTeamRow(node, index))}
          </Directory>
        </div>
        <Keys
          addKey={toggleModalAddKey}
          expireKey={toggleModalExpireKey}
          keys={bot.account.keys.nodes}
        />
      </Collection>
    </>
  );
};

Bot.propTypes = {
  classes: PropType.object.isRequired,
  history: PropType.object.isRequired,
  bot: PropType.object.isRequired
};

export default createFragmentContainer(
  compose(withRouter, withGqlClient, injectSheet(styles))(Bot),
  {
    bot: graphql`
      fragment bot_bot on Bot {
        id
        rowId
        name
        description
        avatar
        account {
          keys(orderBy: $orderKeysBy) {
            nodes {
              id
              rowId
              publicKey
              deprecated
              createdAt
              deprecatedAt
            }
          }
        }
        organization {
          id
          rowId
          name
          canUpdate
        }
        teams {
          totalCount
          nodes {
            id
            rowId
            name
            avatar
            canUpdate
          }
        }
      }
    `
  }
);
